import { useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { CreativeStrategyEntityClass } from '@/services/api';

export const CLASS_LABEL: Record<CreativeStrategyEntityClass, string> = {
  market_concept: 'buyer concept',
  creative_strategy: 'strategy',
  creative_pattern: 'template',
};

const CLASS_TONE: Record<CreativeStrategyEntityClass, string> = {
  market_concept: 'bg-violet-50 text-violet-700 border-violet-200',
  creative_strategy: 'bg-sky-50 text-sky-700 border-sky-200',
  creative_pattern: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function ClassBadge({ entityClass }: { entityClass: CreativeStrategyEntityClass | string }) {
  const known = entityClass in CLASS_LABEL ? (entityClass as CreativeStrategyEntityClass) : null;
  return (
    <span
      className={clsx(
        'inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-medium',
        known ? CLASS_TONE[known] : 'bg-slate-50 text-slate-600 border-slate-200',
      )}
    >
      {known ? CLASS_LABEL[known] : entityClass}
    </span>
  );
}

export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1">{label}</div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2 bg-white">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-navy-900 tabular-nums">{value ?? '—'}</div>
    </div>
  );
}

export function Chip({ children, tone = 'slate', mono }: { children: ReactNode; tone?: 'slate' | 'navy' | 'amber' | 'emerald' | 'red'; mono?: boolean }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    navy: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  } as const;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px]', tones[tone], mono && 'font-mono')}>
      {children}
    </span>
  );
}

export function Section({ title, right, children }: { title: ReactNode; right?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="py-6 text-sm text-slate-500 flex items-center gap-2">
      <Loader2 size={14} className="animate-spin" /> {label}
    </div>
  );
}

export function ErrorNote({ error }: { error: string }) {
  return <div className="py-3 text-sm text-red-700 break-words">{error}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="py-6 text-sm text-slate-500 text-center">{children}</div>;
}

export function WarningBanner({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-amber-600" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** A collapsible block: long prompts, raw JSON. */
export function Collapsible({ label, children, defaultOpen = false }: { label: ReactNode; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-navy-900"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {label}
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  );
}

export function Pre({ children }: { children: ReactNode }) {
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 max-h-96 overflow-auto">
      {children}
    </pre>
  );
}

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDays(days?: number | null): string | null {
  if (days === null || days === undefined) return null;
  return `${Math.round(days)}d`;
}

/** Render an unknown lens value (string, list, dict) as short text. */
export function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o.name === 'string') return o.name;
    return Object.entries(o)
      .map(([k, v]) => `${k}: ${toText(v)}`)
      .join('; ');
  }
  return String(value);
}

export function asList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
}
